import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCreatedJobs,
  getPublishJobsByUID,
} from "../../../services/jobService";
import { Modal, Button, Form, Toast, ToastContainer } from "react-bootstrap";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./ViewCreatedJobs.css";
import ConfirmationAlert from "../../AlertComponent/ConfirmationAlert";
import UpdatePostJobForm from "./UpdatePostJobForm";

const ViewCreatedJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(10);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    bg: "success",
  });

  //States for Filter
  const [showJobTitleFilter, setShowJobTitleFilter] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [searchTitleText, setSearchTitleText] = useState("");

  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [searchDepartmentText, setSearchDepartmentText] = useState("");

  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [searchLocationText, setSearchLocationText] = useState("");

  const [showGradeFilter, setShowGradeFilter] = useState(false);
  const [grades, setGrades] = useState([]);
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [searchGradeText, setSearchGradeText] = useState("");

  const [showFunctionFilter, setShowFunctionFilter] = useState(false);
  const [functions, setFunctions] = useState([]);
  const [filteredFunctions, setFilteredFunctions] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [searchFunctionText, setSearchFunctionText] = useState("");
  const [confirmAlert, setConfirmAlert] = useState(null);

  const navigate = useNavigate();
  const totalRecords = jobs.length;

  // Fetch Jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const data = await getCreatedJobs();
        const createdJobs = data?.data?.createdJobs;
        if (Array.isArray(createdJobs)) {
          setJobs(createdJobs);
        } else {
          throw new Error("Unexpected data format: createdJobs not found");
        }
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleViewDetails = (jobUID) => {
    navigate(`/Dashboard/View-Job-Details/${jobUID}`);
  };

  const handlePublishJob = async (jobUID) => {
    try {
      const response = await getPublishJobsByUID(jobUID);
      if (response?.data?.status === "Success") {
        setToast({
          show: true,
          message: "✅ Job published successfully!",
          bg: "success",
        });
        navigate("/Dashboard/Live-Jobs");
      } else {
        setToast({
          show: true,
          message: "⚠️ Failed to publish job.",
          bg: "danger",
        });
      }
    } catch (error) {
      setToast({
        show: true,
        message: "❌ Error occurred while publishing.",
        bg: "danger",
      });
    }
  };

  const confirmPublishJob = (jobUID) => {
    setConfirmAlert({
      type: "warning",
      message: "Are you sure you want to publish this job?",
      onConfirm: () => handlePublishJob(jobUID),
    });
  };

  // Extract unique filters
  useEffect(() => {
    setJobTitles([...new Set(jobs.map((c) => c.jobTitle))]);
    setDepartments([...new Set(jobs.map((c) => c.department))]);
    setLocations([...new Set(jobs.map((c) => c.location))]);
    setGrades([...new Set(jobs.map((c) => c.grade))]);
    setFunctions([...new Set(jobs.map((c) => c.function))]);
  }, [jobs]);

  // Filter click handlers
  const handleJobTitleFilterClick = () => {
    setShowJobTitleFilter(true);
    setFilteredTitles(jobTitles);
    setSelectedTitles(jobTitles);
    setSearchTitleText("");
  };
  const handleDepartmentFilterClick = () => {
    setShowDepartmentFilter(true);
    setFilteredDepartments(departments);
    setSelectedDepartments(departments);
    setSearchDepartmentText("");
  };
  const handleLocationFilterClick = () => {
    setShowLocationFilter(true);
    setFilteredLocations(locations);
    setSelectedLocations(locations);
    setSearchLocationText("");
  };
  const handleGradeFilterClick = () => {
    setShowGradeFilter(true);
    setFilteredGrades(grades);
    setSelectedGrades(grades);
    setSearchGradeText("");
  };
  const handleFunctionFilterClick = () => {
    setShowFunctionFilter(true);
    setFilteredFunctions(functions);
    setSelectedFunctions(functions);
    setSearchFunctionText("");
  };

  // Filtered jobs
  const filteredJobs = jobs.filter(
    (job) =>
      (selectedTitles.length === 0 || selectedTitles.includes(job.jobTitle)) &&
      (selectedDepartments.length === 0 ||
        selectedDepartments.includes(job.department)) &&
      (selectedLocations.length === 0 ||
        selectedLocations.includes(job.location)) &&
      (selectedGrades.length === 0 || selectedGrades.includes(job.grade)) &&
      (selectedFunctions.length === 0 ||
        selectedFunctions.includes(job.function)) &&
      job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const hasJobs = jobs.length > 0;

  const ColumnFilterModal = ({
    show,
    onHide,
    title,
    items,
    filteredItems,
    setFilteredItems,
    selectedItems,
    setSelectedItems,
    searchText,
    setSearchText,
  }) => (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Filter by {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="text"
          placeholder={`Search ${title}`}
          value={searchText}
          onChange={(e) =>
            setSearchText(e.target.value) ||
            setFilteredItems(
              items.filter((item) =>
                item.toLowerCase().includes(e.target.value.toLowerCase())
              )
            )
          }
          className="mb-3"
        />
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={selectedItems.length === items.length}
            onChange={(e) => setSelectedItems(e.target.checked ? items : [])}
            id="select-all"
          />
          <label className="form-check-label fw-semibold" htmlFor="select-all">
            Select All
          </label>
        </div>
        {filteredItems.map((item, idx) => (
          <div className="form-check" key={idx}>
            <input
              className="form-check-input"
              type="checkbox"
              checked={selectedItems.includes(item)}
              onChange={() =>
                setSelectedItems((prev) =>
                  prev.includes(item)
                    ? prev.filter((t) => t !== item)
                    : [...prev, item]
                )
              }
              id={`${title}-${idx}`}
            />
            <label className="form-check-label" htmlFor={`${title}-${idx}`}>
              {item}
            </label>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onHide}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="jobs-container container mt-5 pt-4">
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toast.bg}
          onClose={() => setToast({ ...toast, show: false })}
          show={toast.show}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white fw-semibold">
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="jobs-card shadow-sm">
        <div className="jobs-header d-flex justify-content-between align-items-center">
          <h3 className="fw-bold mb-0">Draft Jobs</h3>
        </div>

        {/* Search + Create Button */}
        <div className="jobs-toolbar d-flex flex-wrap align-items-center gap-3 my-3 w-100">
          <div
            className="input-group rounded-pill border bg-white shadow-sm"
            style={{ maxWidth: "350px" }}
          >
            <span className="input-group-text bg-transparent border-0 ps-3">
              <i className="bi bi-search text-secondary"></i>
            </span>
            <input
              type="text"
              className="form-control border-0 rounded-5 shadow-none"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <Button
            variant="primary"
            className="fw-semibold rounded-pill px-4 ms-auto d-flex align-items-center"
            onClick={() => navigate("/Dashboard/PostJob")}
          >
            <i className="bi bi-plus-circle me-2"></i> Create Job
          </Button>
        </div>

        {/* Jobs Table or Skeleton */}
        <div className="Overflow-Auto">
          {loading ? (
            <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
              <table className="table modern-table align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Department</th>
                    <th>Location</th>
                    <th>Grade</th>
                    <th>Function</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(5)].map((_, idx) => (
                    <tr key={idx}>
                      {Array(7)
                        .fill()
                        .map((__, i) => (
                          <td key={i}>
                            <Skeleton height={20} />
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </SkeletonTheme>
          ) : (
            <table className="table modern-table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>
                    Title{" "}
                    <i
                      className="bi bi-funnel ms-2 text-primary"
                      onClick={handleJobTitleFilterClick}
                      style={{ cursor: "pointer" }}
                    ></i>
                  </th>
                  <th>
                    Department
                    <i
                      className="bi bi-funnel ms-2 text-primary"
                      onClick={handleDepartmentFilterClick}
                      style={{ cursor: "pointer" }}
                    ></i>
                  </th>
                  <th>
                    Location
                    <i
                      className="bi bi-funnel ms-2 text-primary"
                      onClick={handleLocationFilterClick}
                      style={{ cursor: "pointer" }}
                    ></i>
                  </th>
                  <th>
                    Grade
                    <i
                      className="bi bi-funnel ms-2 text-primary"
                      onClick={handleGradeFilterClick}
                      style={{ cursor: "pointer" }}
                    ></i>
                  </th>
                  <th>
                    Function
                    <i
                      className="bi bi-funnel ms-2 text-primary"
                      onClick={handleFunctionFilterClick}
                      style={{ cursor: "pointer" }}
                    ></i>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentJobs.length > 0 ? (
                  currentJobs.map((job, idx) => (
                    <tr key={job.jobUID || idx}>
                      <td>{indexOfFirstJob + idx + 1}</td>
                       <td
                        className="text-primary fw-bold"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/Dashboard/Update-Job/${job.jobUID}`)}
                      >
                      {job.jobTitle || "Untitled"}</td>
                      <td>{job.department || "N/A"}</td>
                      <td>{job.location || "N/A"}</td>
                      <td>{job.grade || "N/A"}</td>
                      <td>{job.function || "N/A"}</td>
                      <td>
                        <div className="dropdown d-inline mx-3 position-relative">
                          <i
                            className="bi bi-three-dots-vertical"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          ></i>
                          <ul className="dropdown-menu shadow">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleViewDetails(job.jobUID)}
                              >
                                <i className="bi bi-eye me-2"></i>
                                View Details
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => confirmPublishJob(job.jobUID)}
                              >
                                <i className="bi bi-upload me-2"></i>
                                Publish Job
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-danger py-3">
                      {hasJobs ? "No Job found." : "No new Job received."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalRecords > 10 && (
          <div className="d-flex justify-content-end align-items-center mt-3 gap-3">
            <div className="d-flex align-items-center">
              <label className="me-2 fw-semibold">Items Per Page:</label>
              <Form.Select
                size="sm"
                value={jobsPerPage}
                onChange={(e) => {
                  setJobsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-pill shadow-sm"
                style={{ width: "100px" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(totalPages)].map((_, idx) => (
                  <li
                    key={idx}
                    className={`page-item ${
                      currentPage === idx + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(idx + 1)}
                    >
                      {idx + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modals */}
      <ColumnFilterModal
        show={showJobTitleFilter}
        onHide={() => setShowJobTitleFilter(false)}
        title="Job Title"
        items={jobTitles}
        filteredItems={filteredTitles}
        setFilteredItems={setFilteredTitles}
        selectedItems={selectedTitles}
        setSelectedItems={setSelectedTitles}
        searchText={searchTitleText}
        setSearchText={setSearchTitleText}
      />

      <ColumnFilterModal
        show={showDepartmentFilter}
        onHide={() => setShowDepartmentFilter(false)}
        title="Department"
        items={departments}
        filteredItems={filteredDepartments}
        setFilteredItems={setFilteredDepartments}
        selectedItems={selectedDepartments}
        setSelectedItems={setSelectedDepartments}
        searchText={searchDepartmentText}
        setSearchText={setSearchDepartmentText}
      />

      <ColumnFilterModal
        show={showLocationFilter}
        onHide={() => setShowLocationFilter(false)}
        title="Location"
        items={locations}
        filteredItems={filteredLocations}
        setFilteredItems={setFilteredLocations}
        selectedItems={selectedLocations}
        setSelectedItems={setSelectedLocations}
        searchText={searchLocationText}
        setSearchText={setSearchLocationText}
      />

      <ColumnFilterModal
        show={showGradeFilter}
        onHide={() => setShowGradeFilter(false)}
        title="Grade"
        items={grades}
        filteredItems={filteredGrades}
        setFilteredItems={setFilteredGrades}
        selectedItems={selectedGrades}
        setSelectedItems={setSelectedGrades}
        searchText={searchGradeText}
        setSearchText={setSearchGradeText}
      />

      <ColumnFilterModal
        show={showFunctionFilter}
        onHide={() => setShowFunctionFilter(false)}
        title="Function"
        items={functions}
        filteredItems={filteredFunctions}
        setFilteredItems={setFilteredFunctions}
        selectedItems={selectedFunctions}
        setSelectedItems={setSelectedFunctions}
        searchText={searchFunctionText}
        setSearchText={setSearchFunctionText}
      />

      {confirmAlert && (
        <ConfirmationAlert
          title={confirmAlert.type}
          message={confirmAlert.message}
          onClose={() => setConfirmAlert(null)}
          onConfirm={() => {
            confirmAlert.onConfirm();
            setConfirmAlert(null);
          }}
          onCancel={() => setConfirmAlert(null)}
        />
      )}
    </div>
  );
};

export default ViewCreatedJobs;
